import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { ruleAPI } from '../../utils/api';

const TermsAndConditions = () => {
    const navigate = useNavigate();
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTerms();
    }, []);

    const defaultTerms = `
Last updated: ${new Date().toLocaleDateString()}

1. Acceptance of Terms
By accessing or using the Dr. Vivara mobile application and website, you agree to comply with and be bound by these Terms and Conditions. If you do not agree, please do not use our services.

2. Medical Disclaimer (Critical)
DR. VIVARA IS NOT FOR EMERGENCIES. IF YOU BELIEVE YOU HAVE A MEDICAL EMERGENCY, CALL YOUR LOCAL EMERGENCY SERVICES IMMEDIATELY OR GO TO THE NEAREST HOSPITAL. 
The information and services provided are for general wellness and non-emergency consultations. We do not provide medical advice directly; all consultations are performed by independent, licensed healthcare professionals.

3. Nature of Services
Dr. Vivara is a platform that facilitates:
- Telehealth consultations via audio, video, and chat.
- Order management for medicines and healthcare products.
- Access to health records and wellness programs.

4. User Eligibility and Registration
- You must be at least 18 years of age to create an account. Minors may use the service only under the supervision of a parent or legal guardian.
- You agree to provide accurate, current, and complete information during the registration process.
- You are responsible for maintaining the confidentiality of your account credentials.

5. Medicine Delivery and Prescriptions
- Certain medicines require a valid prescription from a registered medical practitioner.
- We facilitate delivery through third-party licensed pharmacies and retailers.
- Availability of products and delivery timelines may vary.

6. Payments and Refunds
- Fees for services are disclosed at the time of booking or transition.
- Payments are processed through secure third-party gateways.
- Refund policies are subject to the specific service provider's terms and will be evaluated on a case-by-case basis.

7. Prohibited Conduct
You agree not to:
- Provide false medical information.
- Use the service for any illegal or unauthorized purpose.
- Attempt to interfere with the proper working of the platform.

8. Limitation of Liability
To the maximum extent permitted by law, Dr. Vivara shall not be liable for any direct, indirect, incidental, or consequential damages resulting from your use or inability to use the services.

9. Termination
We reserve the right to terminate or suspend your account and access to the services at our sole discretion, without notice, for conduct that we believe violates these Terms.

10. Governing Law
These terms are governed by the laws of the jurisdiction in which Dr. Vivara is registered.

11. Contact Information
For any queries regarding these terms, please contact us at support@drvivara.com.
`;

    const fetchTerms = async () => {
        try {
            // Fetch all active rules and find the terms_and_conditions
            const res = await ruleAPI.getActive();
            if (res.data?.success) {
                const termsRule = res.data.data.find(r => r.type === 'terms_and_conditions');
                if (termsRule && termsRule.content.trim() !== '') {
                    setContent(termsRule.content);
                } else {
                    setContent(defaultTerms);
                }
            } else {
                setContent(defaultTerms);
            }
        } catch (error) {
            console.error('Error fetching terms:', error);
            setContent(defaultTerms);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-dark text-light p-6">
            <div className="max-w-4xl mx-auto">
                <div className="flex items-center mb-8">
                    <button
                        onClick={() => navigate(-1)}
                        className="mr-4 text-gold"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h1 className="text-3xl font-bold text-gold">Terms & Conditions</h1>
                </div>

                <div className="bg-gray-800 rounded-xl p-8">
                    {loading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
                        </div>
                    ) : (
                        <div className="prose prose-invert max-w-none">
                            <div className="text-gray-300 leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}></div>
                        </div>
                    )}

                    <div className="mt-8 pt-6 border-t border-gray-700 text-sm text-gray-500">
                        <p>Last updated: {new Date().toLocaleDateString()}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TermsAndConditions;
